import React, {Component} from 'react';
import {connect} from 'react-redux';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import {Delete as DeleteIcon, Add as AddIcon} from '@material-ui/icons';
import {resolveIcon} from '../../data-lineage/util/characteristicIcons';
import CharacteristicIconPicker from '../../data-lineage/components/CharacteristicIconPicker';
import ImpactDialog from './ImpactDialog';
import Auth from '../../../auth/auth';
import * as A from '../../data-lineage/store/lineageActions';

const auth = new Auth();
const EMPTY_FORM = {name: '', icon: 'Storage', color: '#1976d2', description: ''};

class CharacteristicsPanel extends Component {
  state = {
    editing: null,
    form: EMPTY_FORM,
    saving: false,
    usage: {},
    deleting: null,
    usageLoading: false,
    renameId: null,
    renameDraft: '',
  };

  componentDidMount() {
    this.fetchMissingUsage();
  }

  componentDidUpdate(prev) {
    if (prev.characteristics !== this.props.characteristics) {
      this.fetchMissingUsage();
    }
  }

  fetchMissingUsage() {
    const {characteristics, fetchCharacteristicUsage} = this.props;
    characteristics.forEach((c) => {
      if (this.state.usage[c.id] === undefined) {
        this.setState((s) => ({usage: {...s.usage, [c.id]: 'loading'}}));
        fetchCharacteristicUsage(c.id).then((result) => {
          this.setState((s) => ({usage: {...s.usage, [c.id]: result}}));
        }).catch(() => {
          this.setState((s) => ({usage: {...s.usage, [c.id]: null}}));
        });
      }
    });
  }

  startNew = () => this.setState({editing: 'new', form: EMPTY_FORM});
  cancelEdit = () => this.setState({editing: null, form: EMPTY_FORM});

  save = async () => {
    const {editing, form} = this.state;
    if (!form.name.trim()) return;
    this.setState({saving: true});
    if (editing === 'new') {
      const result = await this.props.createCharacteristic(form);
      if (result && result.response) {
        this.setState((s) => ({usage: {...s.usage, [result.response.id]: {nodeCount: 0, diagramCount: 0, diagrams: []}}}));
      }
    }
    this.setState({saving: false, editing: null, form: EMPTY_FORM});
  };

  startRename = (c) => this.setState({renameId: c.id, renameDraft: c.name});
  cancelRename = () => this.setState({renameId: null, renameDraft: ''});

  commitRename = async (c) => {
    const {renameDraft} = this.state;
    if (!renameDraft.trim() || renameDraft.trim() === c.name) {
      this.cancelRename();
      return;
    }
    await this.props.updateCharacteristic(c.id, {
      name: renameDraft.trim(),
      icon: c.icon || 'Storage',
      color: c.color || '#1976d2',
      description: c.description || '',
    });
    this.cancelRename();
  };

  openDelete = async (c) => {
    this.setState({deleting: c, usageLoading: true});
    try {
      const result = await this.props.fetchCharacteristicUsage(c.id);
      this.setState((s) => ({usage: {...s.usage, [c.id]: result}, usageLoading: false}));
    } catch (e) {
      this.setState({usageLoading: false});
    }
  };

  confirmDelete = async () => {
    const {deleting} = this.state;
    if (!deleting) return;
    await this.props.deleteCharacteristic(deleting.id);
    this.setState((s) => {
      const usage = {...s.usage};
      delete usage[deleting.id];
      return {deleting: null, usageLoading: false, usage};
    });
  };

  closeDelete = () => this.setState({deleting: null, usageLoading: false});

  render() {
    const {characteristics} = this.props;
    const {editing, form, saving, usage, deleting, usageLoading, renameId, renameDraft} = this.state;
    const isSuperUser = auth.getIsSuperUser();

    const addBtn = (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={this.startNew}
        disabled={!isSuperUser || !!editing}
        data-testid="lc-char-add-btn"
      >
        Add
      </Button>
    );

    return (
      <div className="lc-panel" data-testid="lc-char-panel">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
          <Typography variant="subtitle2">Characteristics</Typography>
          {isSuperUser ? addBtn : (
            <Tooltip title="Requires super user">{addBtn}</Tooltip>
          )}
        </div>

        {characteristics.length === 0 && (
          <Typography variant="body2" color="textSecondary">No characteristics yet.</Typography>
        )}

        {characteristics.map((c) => {
          const IconCmp = resolveIcon(c.icon);
          const charUsage = usage[c.id];
          const isRenaming = renameId === c.id;

          const deleteBtn = (
            <IconButton
              size="small"
              onClick={() => this.openDelete(c)}
              disabled={!isSuperUser}
              data-testid={`lc-char-delete-${c.id}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );

          return (
            <div
              key={c.id}
              className="lc-panel-row"
              data-testid={`lc-char-row-${c.id}`}
            >
              <IconCmp style={{color: c.color || '#1976d2', fontSize: 20, flexShrink: 0}} />

              {isRenaming ? (
                <TextField
                  size="small"
                  value={renameDraft}
                  autoFocus
                  style={{flex: 1}}
                  onChange={(e) => this.setState({renameDraft: e.target.value})}
                  onBlur={() => this.commitRename(c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') this.commitRename(c);
                    else if (e.key === 'Escape') this.cancelRename();
                  }}
                  inputProps={{'data-testid': `lc-char-rename-${c.id}`}}
                />
              ) : (
                <Typography
                  style={{flex: 1, cursor: isSuperUser ? 'pointer' : 'default'}}
                  title={isSuperUser ? 'Click to rename' : c.name}
                  onClick={() => isSuperUser && this.startRename(c)}
                  data-testid={`lc-char-rename-${c.id}`}
                >
                  {c.name}
                </Typography>
              )}

              <Typography
                variant="caption"
                color="textSecondary"
                style={{whiteSpace: 'nowrap'}}
                data-testid={`lc-char-usage-${c.id}`}
              >
                {charUsage === 'loading' ? <CircularProgress size={10} /> :
                  charUsage ? `${charUsage.diagramCount} diagram(s)` : ''}
              </Typography>

              {isSuperUser ? deleteBtn : (
                <Tooltip title="Requires super user">{deleteBtn}</Tooltip>
              )}
            </div>
          );
        })}

        {editing === 'new' && (
          <div className="lc-panel-form">
            <Typography variant="subtitle2" gutterBottom>New characteristic</Typography>
            <TextField
              label="Name" fullWidth margin="dense" value={form.name}
              onChange={(e) => this.setState({form: {...form, name: e.target.value}})}
              inputProps={{'data-testid': 'lc-char-name-input'}}
            />
            <TextField
              label="Color" fullWidth margin="dense" type="color" value={form.color}
              onChange={(e) => this.setState({form: {...form, color: e.target.value}})}
            />
            <TextField
              label="Description" fullWidth margin="dense" value={form.description}
              onChange={(e) => this.setState({form: {...form, description: e.target.value}})}
            />
            <Typography variant="caption" style={{marginTop: 8, display: 'block'}}>Icon</Typography>
            <CharacteristicIconPicker
              value={form.icon}
              onChange={(icon) => this.setState({form: {...form, icon}})}
            />
            <div style={{marginTop: 10, display: 'flex', gap: 8}}>
              <Button
                size="small" variant="contained" color="primary"
                onClick={this.save}
                disabled={saving || !form.name.trim()}
                data-testid="lc-char-save-btn"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={this.cancelEdit} data-testid="lc-char-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}

        <ImpactDialog
          open={!!deleting}
          onClose={this.closeDelete}
          onConfirm={this.confirmDelete}
          loading={usageLoading}
          usage={deleting ? usage[deleting.id] : null}
          itemName={deleting ? deleting.name : ''}
          itemType="characteristic"
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  characteristics: state.lineageReducer.characteristics || [],
});

export default connect(mapStateToProps, {
  createCharacteristic: A.createCharacteristic,
  updateCharacteristic: A.updateCharacteristic,
  deleteCharacteristic: A.deleteCharacteristic,
  fetchCharacteristicUsage: A.fetchCharacteristicUsage,
})(CharacteristicsPanel);
